import { Link } from 'react-router';
import {
  ArrowRight,
  Banknote,
  BookOpen,
  Clock4,
  FileSignature,
  HardHat,
  Lightbulb,
  PenLine,
  ShieldCheck,
} from 'lucide-react';
import { APP_NAME } from '@helfr/domain';
import { NahledAplikace } from '../components/NahledAplikace.js';
import { VerejnaHlavicka } from '../components/VerejnaHlavicka.js';

/**
 * FUNKCE — samostatná veřejná stránka (záložka z menu landing page): podrobný
 * přehled produktu po kategoriích, psaný stavařským jazykem, každá kategorie
 * s příkladem ze stavby a zvětšitelným náhledem skutečné aplikace.
 * Zásada důvěry: jen fakta o funkcích, žádné sliby úspor ani vymyšlená čísla.
 */

interface Kategorie {
  Ikona: typeof BookOpen;
  nazev: string;
  hook: string;
  body: string[];
  priklad: string;
  obrazek?: string;
  alt?: string;
  /** Barevné ladění sekce (chip + pozadí) — barevná stránka, ne šedý seznam. */
  ton: 'info' | 'accent' | 'success' | 'primary' | 'sporna' | 'warning';
}

const TONY: Record<Kategorie['ton'], { sekce: string; chip: string }> = {
  info: { sekce: 'bg-info-bg', chip: 'bg-info-text text-white' },
  accent: { sekce: 'bg-accent-tint', chip: 'bg-accent text-text-heading' },
  success: { sekce: 'bg-success-bg', chip: 'bg-success-text text-white' },
  primary: { sekce: 'bg-primary-tint', chip: 'bg-primary text-white' },
  sporna: { sekce: 'bg-sporna-bg', chip: 'bg-sporna-text text-white' },
  warning: { sekce: 'bg-warning-bg', chip: 'bg-warning-text text-white' },
};

/** Exportováno i pro prerender (entry-prerender.tsx) — JSON-LD ItemList a llms.txt. */
export const KATEGORIE: Kategorie[] = [
  {
    Ikona: BookOpen,
    ton: 'info',
    nazev: 'Stavební deník',
    hook: 'Průběžně, průkazně a bez dopisování o víkendu.',
    body: [
      'Zápis dne za minutu: diktování, převzetí včerejška jedním ťuknutím, počasí se doplní samo podle polohy stavby.',
      'Fotky s časem pořízení a GPS — důkaz, který se u zakrývaných konstrukcí zpětně už nezíská.',
      'Podpis dne rukou na displeji záznam uzamkne; oprava jen novým opravným záznamem, jako v účetnictví.',
      'Kalendář měsíce ukáže díry v deníku (pracovní den bez záznamu) na jeden pohled.',
      'Montážní deníky subdodavatelů vedle stavebního deníku GD.',
      'Fulltext přes záznamy i popisky fotek; export celého deníku do PDF a Excelu.',
    ],
    priklad:
      'Pátek 15:40, zítra se zakrývá výztuž stropu. Mistr vyfotí armaturu, pošle výzvu TDS k přejímce s termínem a den podepíše prstem. V pondělí je všechno doložené — fotky, časy, podpis.',
    obrazek: '/marketing/denik.png',
    alt: 'Deník v Helfru — kalendář podepsaných dnů a čtečka záznamů',
  },
  {
    Ikona: Banknote,
    ton: 'accent',
    nazev: 'Vícepráce a změny',
    hook: 'Od fotky z výkopu k fakturovatelné změně — bez ztrát po cestě.',
    body: [
      'Nález z terénu: foto + pár slov, funguje i bez signálu (odešle se, až signál bude).',
      'Fronta nálezů pro přípraváře — kvalifikovat, sloučit, nebo zamítnout s důvodem.',
      'Změna se dvěma stranami: ZL na investora (výnos) i na subdodavatele (náklad) — marže je vidět průběžně.',
      'Ocenění: cenová nabídka s rozpadem, vlastní kalkulace hodiny × sazby profesí, nebo ruční částka.',
      'Dopad změny na termín ve dnech — nárok na prodloužení lhůty se sčítá sám.',
      'Hlídače: náklad bez výnosového protějšku, záporná marže, práce provedená bez podpisu, limit § 222 ZZVZ u veřejných zakázek.',
      'Vyjádření TDS/TDI ke změně evidované přímo u ní — razítko dozoru na jednom místě.',
    ],
    priklad:
      'Při výkopu narazíte na starou jímku, která v projektu není. Mistr ji vyfotí a nahlásí — a kancelář z nálezu udělá oceněný změnový list pro investora dřív, než se jáma zasype.',
    obrazek: '/marketing/zmena.png',
    alt: 'Detail změny v Helfru — dvě strany, marže a stavový průvodce',
  },
  {
    Ikona: FileSignature,
    ton: 'success',
    nazev: 'Smlouvy, dodatky a fakturace',
    hook: 'Cesta peněz od SOD k podkladu faktury — nahoru i dolů.',
    body: [
      'Hlavní SOD i subdodavatelské smlouvy; soupis prací se importuje z Excelu a slouží jako ceník pro oceňování.',
      'Dodatky se sestavují z podepsaných změnových listů — podpisem dodatku se změny stanou fakturovatelné.',
      'Zjišťovací protokoly: měsíční čerpání v % proti soupisu i změnovým listům, s PDF a podpisem protistrany.',
      'Pozastávky (zádržné) s tranšemi a spouštěči; předávací protokol dopočte termíny uvolnění a hlídač je připomene.',
      'Milníky s penále (Kč/den, nebo % z ceny Díla) — efektivní termín se posouvá o schválené dopady změn.',
      'Rekapitulace změn pro investora odchází bez vašich nákladů a marží.',
    ],
    priklad:
      'Konec měsíce: přípravář naklikne čerpání proti soupisu, TDS odsouhlasí protokol podpisem přes odkaz — a faktura má průkazný podklad. Za dva roky hlídač připomene, že je uvolnitelná tranše zádržného.',
    obrazek: '/marketing/smlouva.png',
    alt: 'Detail smlouvy v Helfru — milníky s penále a pozastávka s tranšemi',
  },
  {
    Ikona: PenLine,
    ton: 'primary',
    nazev: 'Podpisy bez papíru',
    hook: 'Podepisuje se tam, kde se stojí — ne kde je tiskárna.',
    body: [
      'Podpis rukou: prstem na mobilu, myší na počítači, nebo QR kódem z obrazovky rovnou do telefonu.',
      'Protistrana (subdodavatel, TDS, investor) podepíše přes zabezpečený odkaz — bez registrace a instalace.',
      'Jednorázové odkazy s expirací; každý podpis se ukládá s časem a IP adresou.',
      'Podpisy jsou rovnou v PDF dokumentech: změnové listy, dodatky, zjišťovací i předávací protokoly.',
    ],
    priklad:
      'Subdodavatel podepíše změnový list z kabiny bagru: přijde mu odkaz v SMS nebo mailu, otevře, podepíše prstem — a v kanceláři vidíte podepsáno dřív, než dopije kafe.',
    obrazek: '/marketing/podpis.png',
    alt: 'Veřejná podpisová stránka Helfru — podpis změnového listu prstem',
  },
  {
    Ikona: HardHat,
    ton: 'warning',
    nazev: 'Terén v mobilu',
    hook: 'Pro mistra: velká tlačítka, diktování, funguje v rukavicích i bez signálu.',
    body: [
      'Deník, nálezy, úkoly a vady, výkresy, docházka — všechno palcem na telefonu.',
      'Offline fronta: co se zapíše bez signálu, odejde samo, až signál bude.',
      'Výkresy s revizemi — nová revize PD rovnou nabídne založení nálezu (nejčastější zdroj víceprací).',
      'Úkoly a vady s fotkami, termínem a přiřazením subdodavateli nebo profesi.',
    ],
    priklad:
      'V suterénu bez signálu mistr normálně diktuje zápis a fotí. Jakmile vyjde ven, všechno se samo odešle — nic se neztratí a nic nemusí psát dvakrát.',
  },
  {
    Ikona: Clock4,
    ton: 'sporna',
    nazev: 'Lidé a docházka',
    hook: 'Konec dvojího zápisu papír → mzdový systém.',
    body: [
      'Mistr zapíše docházku za celou partu pár ťuknutími — dělník aplikaci nepotřebuje ani nemá login.',
      'Hodiny se dopočtou z příchodu a odchodu; měsíční přehled osoby × dny v kanceláři.',
      'Excel export pro mzdový systém (OKbase) s osobním číslem — výpočet mezd zůstává tam, kde je.',
      'Počty lidí subdodavatelů „u brány" pro evidenci BOZP.',
    ],
    priklad:
      'Pátek večer: mistr ťukne „Všichni 8 h", u dvou chlapů upraví dřívější odchod a uloží. Mzdová účetní si na konci měsíce stáhne Excel — žádné papírky, žádné přepisování.',
    obrazek: '/marketing/dochazka.png',
    alt: 'Docházka v Helfru — měsíční mřížka osob a dnů s exportem pro OKbase',
  },
  {
    Ikona: ShieldCheck,
    ton: 'info',
    nazev: 'Řízení a bezpečnost dat',
    hook: 'Ředitel vidí, co hoří. Mistr nikdy nevidí peníze.',
    body: [
      'Přehled „co dnes hoří" přes všechny zakázky — hlídače mluví lidsky a rovnou nabízejí akci.',
      'Finanční zeď: role bez oprávnění nikdy nedostane ceny, částky ani marže — vynucuje to server, ne obrazovka.',
      'Role a práva si firma nastaví podle sebe; každá důležitá akce se zapisuje do auditní stopy.',
      'Fotogalerie zakázky: všechny fotky z deníku, nálezů, změn i vad po dnech a objektech.',
    ],
    priklad:
      'Ředitel ráno otevře Přehled: na jedné stavbě svítí náklad subdodavatele bez výnosového protějšku. Jedním klikem otevře změnu a nechá založit ZL na investora — díra se zavře, dokud je čerstvá.',
    obrazek: '/marketing/prehled.png',
    alt: 'Přehled v Helfru — co dnes hoří, hlídače a fronty práce',
  },
];

export function FunkcePage() {
  return (
    <main className="min-h-screen bg-bg">
      <VerejnaHlavicka />

      <section className="bg-primary pb-12 text-white">
        <div className="mx-auto max-w-6xl px-6 pt-8">
          <h1 className="text-3xl font-bold text-balance text-white lg:text-4xl">
            Co Helfr umí — do detailu
          </h1>
          <p className="mt-3 max-w-prose text-lg text-primary-tint">
            Postaveno kolem toho, jak stavba skutečně běží: od zápisu dne přes vícepráce a smlouvy
            až po podklady k fakturaci. Náhledy jsou skutečné obrazovky aplikace — kliknutím je
            zvětšíte.
          </p>
        </div>
      </section>

      {KATEGORIE.map((k, i) => {
        const ton = TONY[k.ton];
        const otoceny = i % 2 === 1;
        return (
          <section key={k.nazev} className={i % 2 === 0 ? ton.sekce : 'bg-surface'}>
            <div className="mx-auto grid max-w-6xl items-start gap-8 px-6 py-12 lg:grid-cols-2 lg:gap-14">
              <div className={otoceny ? 'lg:order-2' : ''}>
                <span
                  className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-bold ${ton.chip}`}
                >
                  <k.Ikona size={16} strokeWidth={2} aria-hidden="true" />
                  {k.nazev}
                </span>
                <h2 className="mt-3 text-2xl font-bold text-balance lg:text-3xl">{k.hook}</h2>
                <ul className="mt-4 flex flex-col gap-2.5">
                  {k.body.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-base">
                      <span
                        aria-hidden="true"
                        className="mt-1.5 size-2 shrink-0 rounded-full bg-accent"
                      />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`flex flex-col gap-4 ${otoceny ? 'lg:order-1' : ''}`}>
                {k.obrazek && k.alt && <NahledAplikace src={k.obrazek} alt={k.alt} />}
                {/* Příklad ze stavby — konkrétní scénář použití, žádné sliby. */}
                <aside className="rounded-xl border border-border bg-surface p-4 shadow-sm">
                  <p className="flex items-center gap-2 text-sm font-bold tracking-wide uppercase">
                    <Lightbulb
                      size={16}
                      strokeWidth={2}
                      aria-hidden="true"
                      className="text-accent-pressed"
                    />
                    Příklad ze stavby
                  </p>
                  <p className="mt-1.5 text-base">{k.priklad}</p>
                </aside>
              </div>
            </div>
          </section>
        );
      })}

      {/* Závěrečné CTA */}
      <section className="bg-primary py-14 text-white">
        <div className="mx-auto flex max-w-6xl flex-col items-start gap-5 px-6">
          <h2 className="max-w-2xl text-2xl font-bold text-balance text-white lg:text-3xl">
            Nejlépe to poznáte na ukázkové stavbě se vzorovými daty.
          </h2>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/registrace"
              className="inline-flex min-h-13 items-center gap-2 rounded-lg bg-accent px-6 text-lg font-bold text-text-heading transition-colors duration-100 hover:bg-accent-hover"
            >
              Založte firmu zdarma
              <ArrowRight size={18} strokeWidth={2.25} aria-hidden="true" />
            </Link>
            <Link
              to="/"
              className="inline-flex min-h-13 items-center rounded-lg border border-white/30 px-6 text-lg font-semibold text-white transition-colors duration-100 hover:bg-white/10"
            >
              Zpět na úvod
            </Link>
          </div>
        </div>
      </section>

      <footer className="bg-primary-pressed py-6 text-center text-sm text-primary-tint">
        {APP_NAME} — stavební deník a hlídač víceprací
      </footer>
    </main>
  );
}
